import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import Typography from '../../components/Typography/Typography';
import TextField from '../../components/TextField/TextField';
import Button from '../../components/Button/Button';
import { authAPI } from '../../services/api';

const VerifyOTP = () => {
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email from navigation state
    if (location.state?.email) {
      setEmail(location.state.email);
    } else {
      // If no email in state, redirect back to reset password
      navigate('/reset-password');
    }
  }, [location.state, navigate]);

  useEffect(() => {
    // Timer for resend functionality
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
      if (otpError) setOtpError('');
    }
  };

  const validateOTP = () => {
    if (!otp) {
      setOtpError('Verification code is required');
      return false;
    } else if (otp.length !== 6) {
      setOtpError('Verification code must be 6 digits');
      return false;
    }
    return true;
  };

  const handleVerify = async () => {
    if (validateOTP()) {
      setIsLoading(true);
      
      try {
        const response = await authAPI.verifyCode({ email, code: otp });
        toast.success(response.message || 'Code verified successfully!');
        setIsCodeVerified(true);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Invalid verification code. Please try again.';
        toast.error(errorMessage);
        setOtpError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResendCode = async () => {
    if (canResend) {
      setIsResending(true);
      
      try {
        const response = await authAPI.resendCode({ email });
        toast.success(response.message || 'Verification code has been resent to your email');
        setResendTimer(60);
        setCanResend(false);
        setOtp('');
        setOtpError('');
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Failed to resend code. Please try again.';
        toast.error(errorMessage);
      } finally {
        setIsResending(false);
      }
    }
  };

  const handleBackToReset = () => {
    navigate('/reset-password');
  };

  const validatePassword = () => {
    let isValid = true;
    
    if (!newPassword) {
      setPasswordError('New password is required');
      isValid = false;
    } else if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      isValid = false;
    } else {
      setPasswordError('');
    }
    
    if (!confirmPassword) {
      setConfirmPasswordError('Please confirm your password');
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      isValid = false;
    } else {
      setConfirmPasswordError('');
    }
    
    return isValid;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    if (passwordError) setPasswordError('');
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (confirmPasswordError) setConfirmPasswordError('');
  };

  const handleResetPassword = async () => {
    if (validatePassword()) {
      setIsResettingPassword(true);
      
      try {
        const response = await authAPI.resetPassword({ 
          email, 
          code: otp, 
          newPassword 
        });
        toast.success(response.message || 'Password reset successfully! Please login with your new password.');
        navigate('/');
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Failed to reset password. Please try again.';
        toast.error(errorMessage);
      } finally {
        setIsResettingPassword(false);
      }
    }
  };

  return (
    <div className='flex-1 flex items-center justify-center px-4 py-8'>
      <div className='w-full max-w-md mx-auto'>
        {/* Verify OTP Modal */}
        <div className='!bg-[#FFFFFF] rounded-3xl border-2 border-[#E2E8F0] shadow-auth_box p-6 sm:p-8 w-full relative overflow-hidden'>
          {/* Decorative border gradient */}
          <div className='absolute inset-0 rounded-3xl pointer-events-none'></div>
          
          {/* Header */}
          <div className='text-center mb-6 sm:mb-8 relative z-10'>
            <Typography
              variant='h1'
              className='mb-2 text-blue-900 font-bold title text-2xl sm:text-2xl md:text-2xl lg:text-3xl'
            >
              {isCodeVerified ? 'Reset Password' : 'Verify Email'}
            </Typography>
          
            <Typography variant='body1' color='grey' className='text-k-grey-text text-[16px] mb-2'>
              {isCodeVerified 
                ? 'Enter your new password below'
                : 'We\'ve sent a 6-digit verification code to'
              }
            </Typography>
            
            <Typography variant='body2'  className='text-[#1621C9] font-medium'>
              {email}
            </Typography>
          </div>

          {/* Form */}
          <div className='space-y-4 sm:space-y-4 relative z-10'>
            {!isCodeVerified ? (
              <>
                {/* OTP Field */}
                <TextField
                  label='Verification Code'
                  type='text'
                  value={otp}
                  onChange={handleOtpChange}
                  error={otpError}
                  required
                  helperText='Enter the 6-digit code sent to your email'
                  placeholder='000000'
                  className='w-full text-center text-2xl tracking-widest'
                  maxLength={6}
                  onClear={() => {
                    setOtp('');
                    setOtpError('');
                  }}
                />

                {/* Verify Button */}
                <div className='pt-2 sm:pt-3'>
                  <Button
                    label={isLoading ? 'Verifying...' : 'Verify Code'}
                    variant='primary'
                    className='w-full h-11 sm:h-12 text-base sm:text-lg font-semibold bg-g_gradient hover:opacity-90 transition-opacity'
                    onClick={handleVerify}
                    disabled={isLoading}
                    loading={isLoading}
                  />
                </div>

                {/* Resend Code */}
                <div className='text-center'>
                  <Typography variant='body2' color='grey' className='text-[14px]'>
                    Didn't receive the code?{' '}
                    {canResend ? (
                      <span 
                        className='text-[#1621C9] cursor-pointer underline font-medium'
                        onClick={handleResendCode}
                      >
                        {isResending ? 'Resending...' : 'Resend Code'}
                      </span>
                    ) : (
                      <span className='text-[#1621C9] font-medium'>
                        Resend in {resendTimer}s
                      </span>
                    )}
                  </Typography>
                </div>
              </>
            ) : (
              <>
                {/* New Password Field */}
                <TextField
                  label='New Password'
                  type='password'
                  value={newPassword}
                  onChange={handlePasswordChange}
                  error={passwordError}
                  required
                  helperText='Password must be at least 8 characters long'
                  className='w-full'
                  showPasswordToggle={true}
                  showPassword={showNewPassword}
                  onTogglePassword={() => setShowNewPassword(!showNewPassword)}
                />

                {/* Confirm Password Field */}
                <TextField
                  label='Confirm New Password'
                  type='password'
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={confirmPasswordError}
                  required
                  helperText=''
                  className='w-full'
                  showPasswordToggle={true}
                  showPassword={showConfirmPassword}
                  onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                />

                {/* Reset Password Button */}
                <div className='pt-2 sm:pt-3'>
                  <Button
                    label={isResettingPassword ? 'Resetting...' : 'Reset Password'}
                    variant='primary'
                    className='w-full h-11 sm:h-12 text-base sm:text-lg font-semibold bg-g_gradient hover:opacity-90 transition-opacity'
                    onClick={handleResetPassword}
                    disabled={isResettingPassword}
                    loading={isResettingPassword}
                  />
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className='mt-5 sm:mt-6 text-center relative z-10'>
            {!isCodeVerified && (
              <Typography variant='body2' color='grey' className='text-black text-[14px]'>
                Wrong email?{' '}
                <span 
                  className='text-[#1621C9] cursor-pointer underline font-medium'
                  onClick={handleBackToReset}
                >
                  Change Email
                </span>
              </Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
